import { Alert, ModalBody } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import useFetchMessageLogByUid from 'components/domain/automation/messageLogs/useFetchMessageLogByUid';
import useFetchSystemTemplate from 'components/domain/automation/systemTemplates/useFetchSystemTemplate';
import FormWithProvider from 'components/forms/form/Form';
import FormFieldset from 'components/forms/form/FormFieldset';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import {
  getFormDefaultValues,
  messageLogViewModalSchema,
} from './MessageLogViewModal.schema';
import MessageLogViewModalFooter from './MessageLogViewModalFooter';
import MessageLogViewModalFormElements from './MessageLogViewModalFormElements';
import useMessageLogViewModalFormSubmit from './useMessageLogViewModalFormSubmit';

interface MessageLogViewModalProps {
  messageLogUid: string;
}

const MessageLogViewModal = ({ messageLogUid }: MessageLogViewModalProps) => {
  const { t } = useTranslation();
  const { onSubmit } = useMessageLogViewModalFormSubmit({ messageLogUid });

  const {
    data: messageLog,
    error,
    isLoading: isLoadingMessageLog,
    isError,
  } = useFetchMessageLogByUid({ messageLogUid });

  const shouldFetchTemplate = !!messageLog?.originalTemplateUid;
  const { data: template, isLoading: isLoadingTemplate } =
    useFetchSystemTemplate({
      systemTemplateUid: messageLog?.originalTemplateUid,
      enabled: shouldFetchTemplate,
    });

  if ((isLoadingTemplate && shouldFetchTemplate) || isLoadingMessageLog) {
    return <LoadingPlaceholder />;
  }

  if (isError && error) {
    return (
      <Alert bsStyle="danger">
        {getErrorMessage({
          apiErrorCode: error.response?.data.apiErrorCode,
          apiErrorMessage: error.response?.data?.apiErrorMessage ?? '',
          baseKey: 'pageAutomations.triggers.apiErrorCodes.',
          t,
        })}
      </Alert>
    );
  }

  return (
    <FormWithProvider
      defaultValues={getFormDefaultValues({ messageLog, template })}
      onSubmit={onSubmit}
      resolver={messageLogViewModalSchema({
        messagingType: template?.messagingType,
      })}
      noValidate
    >
      <FormFieldset disabled={!!messageLog?.isMigrated}>
        <ModalBody>
          <MessageLogViewModalFormElements
            messageLog={messageLog}
            template={template ?? null}
          />
        </ModalBody>
      </FormFieldset>
      <MessageLogViewModalFooter
        isMigrated={!!messageLog?.isMigrated}
        isTriggerDeleted={!!messageLog?.triggerDeleted}
      />
    </FormWithProvider>
  );
};

export default MessageLogViewModal;
